import {
  fundsRewardsManagerRest,
  getPendingRewardsRest,
  getPendingWeeklyRakeSharePayoutsRest,
} from '@/services/rewards/rewards-manager-service';
import { useState } from 'react';
import { Card, CardBody, CardFooter, CardHeader, Tooltip } from 'reactstrap';
import { WizardTransition } from '../token-flow-orchestrator';
import RewardsPayoutsComponents from '@/pages/rake/rewards-payouts-components';
import RakeBackPayoutComponents from '@/pages/rake/rake-back-payouts-components';
import { ContractInfo } from '@/components/ContractInfo';
import { config } from '@repo/common-types';
import { useReadContract } from 'wagmi';
import REDEMPTION_VAULT_JSON from '@repo/common-types/src/abis/redemption-vault.json';
import { useQuery } from '@tanstack/react-query';
import { displayTxResult } from '@/components/utilsDisplay';

const FundsRewardsManager: React.FC<WizardTransition> = ({
  currentState: _currentState,
  setState,
  setToast,
  enabled,
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [show, setShow] = useState<string | undefined>(undefined);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [copyTooltipOpen, setCopyTooltipOpen] = useState<boolean>(false);

  const fundsRewards = () => {
    setSubmitting(true);
    fundsRewardsManagerRest()
      .then(() => {
        setToast('Rewards Manager Funded', 'success');
        setState(8);
      })
      .catch((error) => {
        setToast(`Funding rewards manager failed ${error}`, 'warning');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleCopyWeiValue = async () => {
    if (pendingRewardsData?.totalPhnmNeededWei) {
      try {
        await navigator.clipboard.writeText(pendingRewardsData.totalPhnmNeededWei.toString());
        setToast('Wei value copied to clipboard!', 'success');
        setCopyTooltipOpen(true);
        setTimeout(() => setCopyTooltipOpen(false), 1500);
      } catch (error) {
        setToast('Failed to copy Wei value', 'warning');
      }
    }
  };

  const { data: phnmPrice } = useReadContract({
    address: config.contracts.redemptionVault,
    abi: REDEMPTION_VAULT_JSON.abi,
    functionName: 'tokenPrice',
  });

  console.log('[FundsRewardsManager]phnmPrice', phnmPrice);

  const { data: pendingRewardsData } = useQuery({
    queryKey: ['pending-rewards'],
    queryFn: async () => {
      return getPendingRewardsRest();
    },
  });

  /**
   * TODO: use endpoint to fetch the amount of PHNM tokens needed to pay for rewards
   * We could fetch separately the rakeback total and then the total weekly rake share
   * needed to be paid. Basically fetch every single value needed to calculate the
   * total rewards amount.
   * Weekly rake share needs to take into account the percentage paid in phnm, we also
   * need to fetch the phnm price.
   */

  return (
    <>
      <Card>
        <CardHeader>
          <h2>
            <span className="btn btn-light mx-2">7</span>Funds Rewards Manager
          </h2>
        </CardHeader>
        <CardBody>
          <ContractInfo
            className="col-sm-12 col-lg-3"
            style={{ borderWidth: 1, minWidth: 'fit-content' }}
            contractAddress={config.contracts.rewardsManager}
            contractName="RewardsManager"
            infoConfig={{
              showUsdtBalance: false,
              showPhnmBalance: true,
              variablesToShow: [],
            }}
          />
          {pendingRewardsData && (
            <div className="bg-light mt-3 rounded p-3">
              <h5 className="mb-3">Pending Rewards Summary</h5>
              <div className="row">
                <div className="col-md-12">
                  <p>
                    <strong>Week Starting:</strong> {new Date(pendingRewardsData.weekStartDate).toUTCString()}
                  </p>
                </div>
                <div className="col-md-12">
                  <p>
                    <strong>PHNM Price:</strong> {displayTxResult(phnmPrice)}
                  </p>
                </div>
                <div className="col-md-12">
                  <div className="d-flex align-items-center gap-2">
                    <p className="mb-0">
                      <strong>Total PHNM Needed:</strong>{' '}
                      <span
                        id="phnm-total-tooltip"
                        className="text-decoration-underline"
                        style={{ cursor: 'pointer', textDecorationStyle: 'dotted' }}
                      >
                        {parseFloat(pendingRewardsData.totalPhnmNeeded).toLocaleString()} PHNM
                      </span>
                    </p>
                    <button
                      id="copy-wei-button"
                      className="btn"
                      onClick={handleCopyWeiValue}
                      title="Copy Wei value"
                      style={{ padding: '0' }}
                    >
                      <i className="ri-file-copy-line ri-lg"></i>
                    </button>
                  </div>

                  {/* Tooltip for PHNM breakdown */}
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen}
                    target="phnm-total-tooltip"
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                  >
                    <div className="text-start">
                      <div>
                        <strong>Breakdown:</strong>
                      </div>
                      <div>
                        • Rakeback: {parseFloat(pendingRewardsData.pendingRakebackTotalPhnm).toLocaleString()} PHNM
                      </div>
                      <div>
                        • Rake Share: {parseFloat(pendingRewardsData.pendingWeeklyRakeShareTotalPhnm).toLocaleString()}{' '}
                        PHNM
                      </div>
                      <hr className="my-1" />
                      <div>
                        <small>USDT Breakdown:</small>
                      </div>
                      <div>
                        <small>• Rakeback: ${pendingRewardsData.pendingRakebackTotalUsdt.toLocaleString()}</small>
                      </div>
                      <div>
                        <small>
                          • Rake Share: ${pendingRewardsData.pendingWeeklyRakeShareTotalUsdt.toLocaleString()}
                        </small>
                      </div>
                    </div>
                  </Tooltip>

                  {/* Tooltip for copy confirmation */}
                  <Tooltip
                    placement="top"
                    isOpen={copyTooltipOpen}
                    target="copy-wei-button"
                    toggle={() => setCopyTooltipOpen(!copyTooltipOpen)}
                  >
                    Wei value: {pendingRewardsData.totalPhnmNeededWei?.toString()}
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </CardBody>
        <CardFooter>
          <div className="d-flex justify-content-end gap-2">
            <button
              disabled={submitting || !enabled}
              className="btn btn-primary mx-1"
              onClick={() => {
                if (!enabled) {
                  setToast('Please follow Orchestrator steps', 'warning');
                  return;
                }
                fundsRewards();
              }}
            >
              {submitting ? 'Funding...' : 'Fund Rewards Manager'}
            </button>
          </div>
        </CardFooter>
      </Card>
    </>
  );
};

export default FundsRewardsManager;
